import React from "react";
import { useState, useEffect, useContext } from "react";
import {
  ArrowUp,
  ArrowDown,
  Clock,
  Users,
  AlertCircle,
  CheckCircle2,
  Briefcase,
  Calendar,
  Activity,
  PieChart,
  CreditCard,
  Hash,
  Building,
  Download,
} from "lucide-react";

import { useParams, Link } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import ClientService from "../../../services/ClientService";
import EmployeeService from "../../../services/EmployeeService";
import TicketService from "../../../services/TicketService";
import ProjectService from "../../../services/ProjectService";
import BillingItemsService from "../../../services/BillingItemsService";
import paymentQrLogo from "../../../assets/image/KodeKalp-Payment-QR-Code.jpg";

const Dashboard = () => {
  const { clientId } = useParams(); // <-- Get clientId from URL
  const [clientData, setClientData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectUpdates, setProjectUpdates] = useState("");
  const [stats, setStats] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [ticketFiles, setTicketFiles] = useState([]);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [billingId, setBillingId] = useState([]);
  const [totalBill, setTotalBIll] = useState([]);
  const [viewAll, setViewAll] = useState(true);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [newTicket, setNewTicket] = useState({
    title: "",
    description: "",
    priority: "MEDIUM",
    status: "OPEN",
    assignedTo: [],
  });
  const [formData, setFormData] = useState({
    companyName: "",
    address: "",
  });
  const [userInfo, setUserInfo] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const { user } = useContext(AuthContext);



  const displayedUpdates = viewAll
    ? projectUpdates
    : projectUpdates.slice(0, 3);

  // Fetch projects and employees on component mount
  useEffect(() => {
    fetchProjects();
    fetchEmployees();
  }, []);

  // Function to fetch all projects
  const fetchProjects = async () => {
    try {
      setLoading(true);
      const projectsData = await ProjectService.getAllProjects();
      setProjects(projectsData);

      // Select the first project by default if available
      if (projectsData.length > 0) {
        setSelectedProject(projectsData[0]);
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError("Failed to load projects. Please try again later.");
      setLoading(false);
    }
  };

  // Function to fetch all employees
  const fetchEmployees = async () => {
    try {
      const employeesData = await EmployeeService.getAllEmployees();
      setEmployees(employeesData);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setError("Failed to load employees. Please try again later.");
    }
  };

  // Function to fetch tickets by project ID
  const fetchTicketsByProject = async (projectId) => {
    try {
      setLoading(true);
      console.log("projectId - fetchTicketsByProject", projectId);

      const ticketsData = await TicketService.getTicketsByProject(projectId);
      setTickets(ticketsData);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setError("Failed to load tickets. Please try again later.");
      setLoading(false);
    }
  };

  // Function to fetch all tickets across all projects
  const fetchAllTickets = async () => {
    try {
      setTicketsLoading(true);
      const allTickets = [];

      // If we have projects data, get tickets for each project
      if (projects.length > 0) {
        // Create an array of promises for fetching tickets from each project
        const ticketPromises = projects.map((project) =>
          TicketService.getTicketsByProject(project._id)
            .then((projectTickets) => {
              // Add project information to each ticket for reference
              return projectTickets.map((ticket) => ({
                ...ticket,
                projectName: project.name,
              }));
            })
            .catch((err) => {
              console.error(
                `Error fetching tickets for project ${project._id}:`,
                err
              );
              return []; // Return empty array if this specific project fails
            })
        );

        // Wait for all ticket fetching to complete
        const ticketsArrays = await Promise.all(ticketPromises);

        // Flatten the array of arrays into a single array of tickets
        const combinedTickets = ticketsArrays.flat();

        // Sort tickets by creation date (newest first)
        combinedTickets.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        setTickets(combinedTickets);
      }

      setTicketsLoading(false);
    } catch (err) {
      console.error("Error fetching all tickets:", err);
      setError("Failed to load tickets. Please try again later.");
      setTicketsLoading(false);
    }
  };

  //Fetch The Client Data
  useEffect(() => {
    setUserInfo(user);
    console.log("user", user, user?.role);
    console.log("clientId", clientId);

    // Check if user has admin privileges
    const adminCheck = ["ADMIN", "CEO", "VAIBHAV", "EMP"].includes(user?.role);
    setIsAdmin(adminCheck);

    // Fetch client profile data
    const fetchClientData = async () => {
      try {
        let data;
        let statsData = [];

        if (clientId && adminCheck) {
          // Admin viewing a specific client
          data = await ClientService.getClientById(clientId);

          // For admin viewing a specific client, we'll focus on that client's projects
          if (data && data.project) {
            const activeProjects = data.project.filter(
              (p) => p.status === "ACTIVE"
            ).length;
            const previousActiveProjects = activeProjects - 1; // Assume 1 project was added recently

            // Get open tickets and tasks due today (if you have this data)
            // For this example, we'll create placeholder calculations
            const openTickets = data.project.reduce(
              (count, project) =>
                count +
                (project.projectUpdate ? project.projectUpdate.length : 0),
              0
            );
            const previousOpenTickets = openTickets + 5; // Assume 5 tickets were closed

            // For tasks, we would need additional data that isn't shown in your sample
            // Here we're using placeholder values
            const tasksDueToday = 3;
            const previousTasksDueToday = 0; // Assume no tasks were due last week

            // For a single client, there's just one active client
            const activeClients = 1;
            const previousActiveClients = 1; // No change for a single client view

            statsData = [
              {
                title: "Active Projects",
                value: activeProjects,
                icon: <Briefcase size={20} />,
                change: `${activeProjects > previousActiveProjects ? "+" : ""}${
                  activeProjects - previousActiveProjects
                }`,
                isPositive: activeProjects >= previousActiveProjects,
              },
              {
                title: "Open Updates",
                value: openTickets,
                icon: <AlertCircle size={20} />,
                change: `${
                  openTickets < previousOpenTickets ? "+" : ""
                }${Math.abs(openTickets - previousOpenTickets)}`,
                isPositive: openTickets < previousOpenTickets,
              },
              {
                title: "Tasks Due Today",
                value: tasksDueToday,
                icon: <Clock size={20} />,
                change: `${tasksDueToday > previousTasksDueToday ? "+" : ""}${
                  tasksDueToday - previousTasksDueToday
                }`,
                isPositive: tasksDueToday <= previousTasksDueToday,
              },
              {
                title: "Open Tickets",
                value: data.project.reduce((count, project) => {
                  // Calculate open tickets across all projects
                  console.log("tickets: ", tickets);
                  return (
                    count +
                    (project.projectUpdate ? project.projectUpdate.length : 0)
                  );
                }, 0),
                icon: <AlertCircle size={20} />,
                change: "-29",
                isPositive: true,
              },
            ];
          }
        } else {
          // User viewing own profile
          data = await ClientService.getCurrentProfile();

          // For a client viewing their own profile
          if (data && data.project) {
            const activeProjects = data.project.filter(
              (p) => p.status === "ACTIVE"
            ).length;
            const completedProjects = data.project.filter(
              (p) => p.status === "COMPLETED"
            ).length;
            const totalProjects = data.project.length;

            const recentUpdates = data.project.reduce((count, project) => {
              if (project.projectUpdate) {
                const lastWeekUpdates = project.projectUpdate.filter(
                  (update) => {
                    const updateDate = new Date(update.createdAt);
                    const oneWeekAgo = new Date();
                    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                    return updateDate >= oneWeekAgo;
                  }
                );
                return count + lastWeekUpdates.length;
              }
              return count;
            }, 0);

            // Calculate project progress if you have the data
            // Here we're using placeholder calculations
            let projectProgress = 0;
            if (totalProjects > 0) {
              projectProgress = Math.round(
                (completedProjects / totalProjects) * 100
              );
            }

            statsData = [
              {
                title: "Active Projects",
                value: activeProjects,
                icon: <Briefcase size={20} />,
                change: "+0",
                isPositive: true,
              },
              {
                title: "Recent Updates",
                value: recentUpdates,
                icon: <AlertCircle size={20} />,
                change: `+${recentUpdates}`,
                isPositive: true,
              },
              {
                title: "Closed Tickets",
                value: data.project.reduce((count, project) => {
                  if (tickets) {
                    return (
                      count +
                      tickets.filter((ticket) =>
                        ["RESOLVED", "CLOSED", "DONE"].includes(ticket.status)
                      ).length
                    );
                  }
                  return count;
                }, 0),
                icon: <CheckCircle2 size={20} />,
                change: "+13",
                isPositive: true,
              },
              {
                title: "Open Tickets",
                value: data.project.reduce((count, project) => {
                  console.log("project.tickets: ", tickets);
                  if (tickets) {
                    return (
                      count +
                      tickets.filter((ticket) =>
                        [
                          "OPEN",
                          "IN_PROGRESS",
                          "PENDING",
                          "TODO",
                          "REVIEW",
                        ].includes(ticket.status)
                      ).length
                    );
                  }
                  return count;
                }, 0),
                icon: <AlertCircle size={20} />,
                change: "-2",
                isPositive: true,
              },
            ];
          }
        }

        setStats(statsData);
        console.log(data, "data");

        //console.log(data.billingItem );

        setClientData(data);

        setBillingId(data.billingItem);

        setFormData({
          companyName: data.companyName || "",
          address: data.address || "",
        });

        // Transform project updates from the backend data
        const formattedUpdates = [];

        if (data.project && data.project.length > 0) {
          data.project.forEach((project) => {
            if (project.projectUpdate && project.projectUpdate.length > 0) {
              project.projectUpdate.forEach((update) => {
                // Format date to be more readable
                const updateDate = new Date(update.createdAt);
                const today = new Date();

                let dateString;
                if (updateDate.toDateString() === today.toDateString()) {
                  dateString = `Today at ${updateDate.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`;
                } else if (
                  updateDate.toDateString() ===
                  new Date(today.setDate(today.getDate() - 1)).toDateString()
                ) {
                  dateString = `Yesterday at ${updateDate.toLocaleTimeString(
                    [],
                    { hour: "2-digit", minute: "2-digit" }
                  )}`;
                } else {
                  dateString = updateDate.toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                }

                formattedUpdates.push({
                  project: project.name,
                  client: data.companyName,
                  update: update.content,
                  date: dateString,
                  title: update.title,
                });
              });
            }
          });
        }

        // Sort updates by date (newest first)
        formattedUpdates.sort((a, b) => new Date(b.date) - new Date(a.date));

        setProjectUpdates(formattedUpdates);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching client data:", err);
        setError("Failed to load client profile.");
        setLoading(false);
      }
    };

    fetchClientData();
  }, [user, clientId, tickets]);

  //Fetch Bill

  const fetchTotalBill = async () => {
    try {
      setLoading(true);
      if (billingId && billingId.length > 0) {
        const mainBill = await Promise.all(
          billingId.map((id) => BillingItemsService.getBillingItemById(id))
        );
        setTotalBIll(mainBill);
        console.log("Billing Details:", mainBill);
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching billing details:", err);
      setError("Failed to load billing information.");
      setLoading(false);
    }
  };

  // Fetch all tickets when projects are loaded
  useEffect(() => {
    if (projects.length > 0) {
      if (billingId && billingId.length > 0) {
        fetchTotalBill();
      }
    }
  }, [billingId]);

  // Fetch all tickets when projects are loaded
  useEffect(() => {
    if (projects.length > 0) {
      fetchAllTickets();
    }
  }, [projects]);

  // Helper function to calculate duration in months
  const getDurationInMonths = (startDate) => {
    if (!startDate) return 0;

    const start = new Date(startDate);
    const current = new Date();

    const diffYears = current.getFullYear() - start.getFullYear();
    const diffMonths = current.getMonth() - start.getMonth();

    return diffYears * 12 + diffMonths;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "LOW":
        return "bg-gray-100 text-gray-800";
      case "MEDIUM":
        return "bg-blue-100 text-blue-800";
      case "HIGH":
        return "bg-orange-100 text-orange-800";
      case "URGENT":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "OPEN":
        return "bg-blue-100 text-blue-800";
      case "IN_PROGRESS":
        return "bg-yellow-100 text-yellow-800";
      case "PENDING":
        return "bg-purple-100 text-purple-800";
      case "RESOLVED":
        return "bg-green-100 text-green-800";
      case "CLOSED":
        return "bg-gray-100 text-gray-800";
      case "TODO":
        return "bg-gray-100 text-gray-800";
      case "REVIEW":
        return "bg-indigo-100 text-indigo-800";
      case "DONE":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="flex-1 bg-gray-50 p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        <p className="text-gray-600">
          Welcome back! Here's what's happening today.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-sm p-6 flex items-center justify-between"
          >
            <div>
              <p className="text-sm font-medium text-gray-600">{stat.title}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stat.value}
              </p>
              <div
                className={`flex items-center mt-1 ${
                  stat.isPositive ? "text-green-600" : "text-red-600"
                }`}
              >
                {stat.isPositive ? (
                  <ArrowUp size={16} />
                ) : (
                  <ArrowDown size={16} />
                )}
                <span className="text-xs font-medium ml-1">
                  {/* {stat.change} from last week */}
                  latest update
                </span>
              </div>
            </div>
            <div
              className={`rounded-full p-3 ${
                stat.isPositive
                  ? "bg-green-100 text-green-600"
                  : "bg-blue-100 text-blue-600"
              }`}
            >
              {stat.icon}
            </div>
          </div>
        ))}
      </div>

      {/* Two-column layout for main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column - Recent tickets and upcoming tasks */}
        <div className="lg:col-span-2 space-y-8">
          {/* Payment Summary */}
          {totalBill && totalBill.length > 0 && (
            <div className="bg-gray-50 rounded-md">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">
                Payment Summary
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <p className="text-sm font-medium text-gray-500">
                    Total Billed
                  </p>
                  <p className="text-2xl font-bold text-gray-800">
                    {totalBill
                      .reduce(
                        (total, item) => total + parseFloat(item.amount),
                        0
                      )
                      .toLocaleString()}{" "}
                    {totalBill[0]?.currency || "INR"}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <p className="text-sm font-medium text-gray-500">
                    Total Paid
                  </p>
                  <p className="text-2xl font-bold text-green-600">
                    {totalBill
                      .reduce((total, item) => {
                        const paidAmount = item.paidAmount
                          ? item.paidAmount.reduce(
                              (sum, payment) =>
                                sum + parseFloat(payment.amount),
                              0
                            )
                          : 0;
                        return total + paidAmount;
                      }, 0)
                      .toLocaleString()}{" "}
                    {totalBill[0]?.currency || "INR"}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <p className="text-sm font-medium text-gray-500">
                    Outstanding Balance
                  </p>
                  <p className="text-2xl font-bold text-red-600">
                    {totalBill
                      .reduce((total, item) => {
                        const paidAmount = item.paidAmount
                          ? item.paidAmount.reduce(
                              (sum, payment) =>
                                sum + parseFloat(payment.amount),
                              0
                            )
                          : 0;
                        return total + (parseFloat(item.amount) - paidAmount);
                      }, 0)
                      .toLocaleString()}{" "}
                    {totalBill[0]?.currency || "INR"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Recent Tickets */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-semibold text-gray-800 flex items-center">
                <AlertCircle size={18} className="mr-2 text-gray-600" />
                Recent Tickets
              </h2>
              <button className="text-sm font-medium text-blue-600 hover:text-blue-800">
                <Link to={`/dashboard/ticket`}>View All</Link>
              </button>
            </div>
            <div className="bg-white shadow rounded-lg overflow-hidden">
              {ticketsLoading ? (
                <div className="flex justify-center p-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ticket ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Priority
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {tickets.length > 0 ? (
                        tickets.map((ticket) => (
                          <tr key={ticket._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <Link to={`/dashboard/tickets/${ticket._id}`}>
                                {ticket.kkgtTicketId}
                              </Link>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {ticket.title}
                              <p className="text-xs text-gray-500">
                                {" "}
                                ( {ticket.project.name} )
                              </p>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(
                                  ticket.priority
                                )}`}
                              >
                                {ticket.priority}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${getStatusColor(
                                  ticket.status
                                )}`}
                              >
                                {ticket.status.replace("_", " ")}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(ticket.createdAt).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                }
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="5"
                            className="px-6 py-4 text-center text-sm text-gray-500"
                          >
                            "No tickets found for this project"
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Upcoming Tasks */}
          {/* <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-semibold text-gray-800 flex items-center">
                <Clock size={18} className="mr-2 text-gray-600" />
                Upcoming Tasks
              </h2>
              <button className="text-sm font-medium text-blue-600 hover:text-blue-800">
                View All
              </button>
            </div>
            <ul className="divide-y divide-gray-200">
              {upcomingTasks.map((task, index) => (
                <li key={index} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div
                        className={`w-2 h-2 rounded-full mr-3 ${
                          task.status === "TODO"
                            ? "bg-gray-400"
                            : task.status === "IN_PROGRESS"
                            ? "bg-yellow-400"
                            : task.status === "REVIEW"
                            ? "bg-indigo-400"
                            : "bg-green-400"
                        }`}
                      ></div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {task.title}
                        </p>
                        <p className="text-xs text-gray-500">{task.project}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          task.status
                        )}`}
                      >
                        {task.status.replace("_", " ")}
                      </span>
                      <span className="ml-4 text-xs text-gray-500 flex items-center">
                        <Calendar size={14} className="mr-1" />
                        {task.dueDate}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div> */}
        </div>

        {/* Right column - Project updates and activities */}
        <div className="space-y-8">
          {/* Project Updates */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-semibold text-gray-800 flex items-center">
                <Briefcase size={18} className="mr-2 text-gray-600" />
                Project Updates
              </h2>
              <button
                className="text-sm font-medium text-blue-600 hover:text-blue-800"
                onClick={() => setViewAll(!viewAll)}
              >
                {viewAll ? "Show Less" : "View All"}
              </button>
            </div>

            <div
              className={`${
                viewAll ? "max-h-96 overflow-y-auto" : "max-h-full"
              }`}
            >
              <ul className="divide-y divide-gray-200">
                {displayedUpdates.length > 0 ? (
                  displayedUpdates.map((update, index) => (
                    <li key={index} className="px-6 py-4 hover:bg-gray-50">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 pt-1">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                            <Activity size={16} />
                          </div>
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-900">
                            {update.project}
                          </p>
                          <p className="text-xs text-gray-500 mb-1">
                            {update.client}
                          </p>
                          <p className="text-sm font-medium text-gray-700">
                            {update.title}
                          </p>
                          <p className="text-sm text-gray-600">
                            {update.update}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {update.date}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))
                ) : (
                  <li className="px-6 py-4 text-center text-gray-500">
                    No project updates available.
                  </li>
                )}
              </ul>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="space-y-4">
            {/* Completed Tasks Card */}
            {/* <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-700">
                  Completed Tasks
                </h3>
                <span className="text-green-600">
                  <CheckCircle2 size={18} />
                </span>
              </div>
              <div className="flex items-end">
                <span className="text-2xl font-bold text-gray-900">27</span>
                <span className="text-sm text-gray-500 ml-2">this week</span>
              </div>
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: "75%" }}
                  ></div>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  75% of weekly goal
                </div>
              </div>
            </div> */}

            {/* Client Satisfaction Card */}
            {/* <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-700">
                  Client Satisfaction
                </h3>
                <span className="text-blue-600">
                  <Users size={18} />
                </span>
              </div>
              <div className="flex items-end">
                <span className="text-2xl font-bold text-gray-900">4.8</span>
                <span className="text-xs text-gray-500 ml-2">/ 5.0</span>
              </div>
              <div className="mt-2 flex items-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg
                    key={star}
                    className={`w-4 h-4 ${
                      star <= 4 ? "text-yellow-400" : "text-gray-300"
                    }`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
                <span className="text-xs text-gray-500 ml-2">
                  Based on recent feedback
                </span>
              </div>
            </div> */}

            {/* Account Details */}
            <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <Briefcase size={20} className="mr-2 text-blue-600" />
                  Account Details
                </h3>
                <span className="bg-blue-100 p-2 rounded-full text-blue-600">
                  <CreditCard size={18} />
                </span>
              </div>

              {/* <div className="border-b pb-4 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-500">
                    Account Rating
                  </span>
                  <div className="flex items-center">
                    <span className="text-lg font-bold text-blue-600 mr-1">
                      4.8
                    </span>
                    <span className="text-xs text-gray-500">/ 5.0</span>
                  </div>
                </div>
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: "96%" }}
                    ></div>
                  </div>
                </div>
              </div> */}

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Account Number</p>
                  <p className="text-sm font-semibold text-gray-800 flex items-center">
                    <Hash size={14} className="mr-1 text-blue-500" />
                    4549120020000041
                  </p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">IFSC Code</p>
                  <p className="text-sm font-semibold text-gray-800 flex items-center">
                    <Building size={14} className="mr-1 text-blue-500" />
                    UJVN0004549
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    QR Code
                  </span>
                  <span className="text-xs text-blue-600 flex items-center cursor-pointer">
                    <Download size={14} className="mr-1" />
                    Download
                  </span>
                </div>
                <div className="flex justify-center bg-white p-2 rounded-lg border-2 border-gray-100">
                  <div className="relative aspect-square w-32 h-32 overflow-hidden rounded-md">
                    <img
                      src={paymentQrLogo}
                      alt="Payment QR Code"
                      className="object-cover transition-transform hover:scale-105"
                    />
                  </div>
                </div>
                <p className="text-xs text-center text-gray-500 mt-2">
                  Scan to make instant payment
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
